import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, message, Spin, Image, Button, Space } from 'antd';
import { DeleteOutlined, DragOutlined, PlusOutlined } from '@ant-design/icons';
import ImageSelector from '../ImageSelector';
import { useTranslation } from '../../hooks/useTranslation';
import './index.css';

const { TextArea } = Input;

interface EditGalleryModalProps {
  open: boolean;
  galleryId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface GalleryDetail {
  gallery: {
    id: number;
    galleryId: string;
    title: string;
    bannerUrl?: string;
    description?: string;
    announcement?: string;
    imageCount: number;
    createTime: string;
  };
  images: string[];
}

const EditGalleryModal: React.FC<EditGalleryModalProps> = ({
  open,
  galleryId,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(true);
  const [images, setImages] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);

  // 加载图集详情
  const loadGalleryDetail = async () => {
    setDetailLoading(true);
    try {
      const response = await fetch(`/gallery/detail/${galleryId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        const data: GalleryDetail = result.data;
        form.setFieldsValue({
          title: data.gallery.title,
          bannerUrl: data.gallery.bannerUrl || '',
          description: data.gallery.description || '',
          announcement: data.gallery.announcement || '',
        });
        setImages(data.images || []);
      } else {
        message.error(t('editGallery.loadFailed', { message: result.message || t('error.unknownError') }));
        onClose();
      }
    } catch (error) {
      message.error(t('editGallery.loadFailedNetwork'));
      onClose();
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (open && galleryId) {
      loadGalleryDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, galleryId]);

  // 表单提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      console.log('提交的图片顺序:', images); // 调试日志

      const response = await fetch('/gallery/update', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          galleryId: galleryId,
          title: values.title,
          bannerUrl: values.bannerUrl,
          description: values.description,
          announcement: values.announcement,
          imgs: images
        })
      });

      const result = await response.json();
      if (result.success) {
        message.success(t('editGallery.updateSuccess'));
        onSuccess();
        onClose();
      } else {
        message.error(t('editGallery.updateFailed', { message: result.message || t('error.unknownError') }));
      }
    } catch (error: any) {
      if (error.errorFields) {
        message.warning(t('editGallery.fillRequired'));
      } else {
        message.error(t('editGallery.updateFailedNetwork'));
        console.error('更新失败:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // 删除图片
  const handleDeleteImage = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  // 拖拽开始
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  // 拖拽结束
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // 拖拽过程中
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];

    // 移除原位置的图片
    newImages.splice(draggedIndex, 1);
    // 插入到新位置
    newImages.splice(index, 0, draggedImage);

    console.log('拖拽后的顺序:', newImages); // 调试日志
    setImages(newImages);
    setDraggedIndex(index);
  };

  // 从上传历史中选择图片
  const handleSelectImages = (selectedUrls: string[]) => {
    // 过滤掉已存在的图片
    const newUrls = selectedUrls.filter(url => !images.includes(url));

    if (newUrls.length === 0) {
      message.info(t('editGallery.imagesAlreadyInGallery'));
      return;
    }

    setImages([...images, ...newUrls]);
    message.success(t('editGallery.imagesAdded', { count: newUrls.length }));
  };

  // URL校验
  const validateUrl = (_: any, value: string) => {
    if (!value) {
      return Promise.resolve();
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(t('editGallery.invalidUrl')));
  };

  return (
    <Modal
      title={t('editGallery.title')}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={800}
      destroyOnClose
      maskClosable={false}
      styles={{
        body: { paddingTop: 16, paddingBottom: 16 }
      }}
    >
      {detailLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p>{t('editGallery.loading')}</p>
        </div>
      ) : (
        <>
          <Form
            form={form}
            layout="vertical"
            autoComplete="off"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              label={t('editGallery.titleLabel')}
              name="title"
              rules={[{ required: true, message: t('editGallery.titleRequired') }]}
              style={{ marginBottom: 12 }}
            >
              <Input placeholder={t('editGallery.titlePlaceholder')} maxLength={500} />
            </Form.Item>

            <Form.Item
              label={t('editGallery.bannerUrlLabel')}
              name="bannerUrl"
              rules={[{ validator: validateUrl }]}
              style={{ marginBottom: 12 }}
            >
              <Input placeholder={t('editGallery.bannerUrlPlaceholder')} />
            </Form.Item>

            <Form.Item
              label={t('editGallery.descriptionLabel')}
              name="description"
              style={{ marginBottom: 12 }}
            >
              <TextArea
                placeholder={t('editGallery.descriptionPlaceholder')}
                rows={2}
                maxLength={2000}
              />
            </Form.Item>

            <Form.Item
              label={t('editGallery.announcementLabel')}
              name="announcement"
              style={{ marginBottom: 0 }}
            >
              <TextArea
                placeholder={t('editGallery.announcementPlaceholder')}
                rows={2}
                maxLength={1000}
              />
            </Form.Item>
          </Form>

          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <h4 style={{ margin: 0, fontSize: '14px' }}>{t('editGallery.imageList', { count: images.length })}</h4>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setImageSelectorOpen(true)}
              >
                {t('editGallery.selectImages')}
              </Button>
            </div>

            <div className="image-list">
              {images.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  {t('editGallery.noImages')}
                </div>
              ) : (
                <div className="image-grid">
                  {images.map((url, index) => (
                    <div
                      key={index}
                      className="image-item"
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, index)}
                    >
                      <Image
                        src={url}
                        alt={t('editGallery.imageAlt', { index: index + 1 })}
                        width="100%"
                        height={120}
                        style={{ objectFit: 'cover' }}
                        preview={{ src: url }}
                      />
                      <div className="image-overlay">
                        <Space>
                          <DragOutlined style={{ fontSize: '16px', color: '#fff', cursor: 'move' }} />
                          <DeleteOutlined
                            style={{ fontSize: '16px', color: '#fff', cursor: 'pointer' }}
                            onClick={() => handleDeleteImage(index)}
                          />
                        </Space>
                      </div>
                      <div className="image-index">{index + 1}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* 图片选择器弹窗 */}
      <ImageSelector
        open={imageSelectorOpen}
        onClose={() => setImageSelectorOpen(false)}
        onSelect={handleSelectImages}
        currentImages={images}
      />
    </Modal>
  );
};

export default EditGalleryModal;
